import { useCallback, useMemo, useState } from 'react';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import {
  formatSiren,
  normalizeOrganizationIdentifierValue,
} from '@e-socna/third-parties';

import { formatDate } from '../../utils/format-date.util';
import { EllipsisTooltip } from '../../components/EllipsisTooltip';
import { RightDrawer } from '../../components/RightDrawer';
import { JSONPretty } from '../../components/JSONPretty';
import { useNafEntriesQuery } from '../../forms/ThirdParty/hooks/useNafEntriesQuery.hook';
import { useLegalStatusesQuery } from '../../forms/ThirdParty/hooks/useLegalStatusesQuery.hook';

import { resolveTradingName } from './mappers/organization-search-result.mapper';
import { matchLegalStatusFromNatureJuridique } from './mappers/legal-status-code.mapper';
import { useExistingOrganizationIdentifiers } from './hooks/useExistingOrganizationIdentifiers.hook';
import { buildEstablishmentsList } from './utils/build-establishments-list.util';
import { formatDirectorName } from './utils/format-director-name.util';
import { isActiveAdministrativeStatus } from './utils/is-active-administrative-status.util';
import { isPhysicalPersonDirector } from './utils/is-physical-person-director.util';
import { resolveHeadOfficeEstablishment } from './utils/resolve-head-office-establishment.util';
import { resolveNafLabel } from './utils/resolve-naf-label.util';
import { EstablishmentItem } from './EstablishmentItem';
import type { OrganizationSearchResult } from './types/organization-search-result.type';

interface Props {
  readonly isCreateOrganizationDisabled?: boolean;
  readonly onCreateOrganization: (result: OrganizationSearchResult) => void;
  readonly result: OrganizationSearchResult;
}

export const ResultItem = ({
  isCreateOrganizationDisabled,
  onCreateOrganization,
  result,
}: Readonly<Props>) => {
  const { data: legalStatusesData } = useLegalStatusesQuery();
  const legalStatuses =
    legalStatusesData?.findAllActiveLegalStatuses ?? EMPTY_ARRAY;
  const { data: nafEntriesData } = useNafEntriesQuery();
  const existingOrganizationIdentifiers = useExistingOrganizationIdentifiers();

  const nafDesignationByCode = useMemo(
    () =>
      new Map(
        (nafEntriesData?.findAllActiveNafEntries ?? EMPTY_ARRAY).map(
          (nafEntry) => [nafEntry.code, nafEntry.designation],
        ),
      ),
    [nafEntriesData],
  );

  const directors = result.dirigeants ?? EMPTY_ARRAY;
  const isActive = isActiveAdministrativeStatus(result.etat_administratif);
  const headOfficeEstablishment = resolveHeadOfficeEstablishment(result);
  const tradingName = headOfficeEstablishment
    ? resolveTradingName(result, headOfficeEstablishment)
    : undefined;
  const vatNumber = result.tva?.[0];
  const legalStatusLabel = matchLegalStatusFromNatureJuridique(
    result.nature_juridique,
    legalStatuses,
  )?.label;
  const activityLabel = resolveNafLabel(
    result.activite_principale_naf25,
    result.activite_principale,
    nafDesignationByCode,
  );

  const establishments = useMemo(
    () => buildEstablishmentsList(result),
    [result],
  );

  const handleCreateOrganization = useCallback(() => {
    onCreateOrganization(result);
  }, [onCreateOrganization, result]);

  const [isRawDataDrawerOpen, setIsRawDataDrawerOpen] = useState(false);
  const openRawDataDrawer = useCallback(() => {
    setIsRawDataDrawerOpen(true);
  }, []);
  const closeRawDataDrawer = useCallback(() => {
    setIsRawDataDrawerOpen(false);
  }, []);

  const organizationLabel = `${result.nom_raison_sociale ?? result.nom_complet}${result.sigle ? ` (${result.sigle})` : EMPTY_STRING}`;

  return (
    <Card
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      variant="outlined"
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <EllipsisTooltip title={organizationLabel}>
            <Typography
              component="h2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant="h6"
            >
              {organizationLabel}
            </Typography>
          </EllipsisTooltip>
          <Chip
            color={isActive ? 'success' : 'default'}
            label={isActive ? 'Active' : 'Fermée'}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        </Stack>

        {tradingName ? (
          <Typography sx={{ mb: 1 }} variant="subtitle2">
            {tradingName}
          </Typography>
        ) : null}

        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography color="text.secondary" variant="body2">
            SIREN {formatSiren(result.siren)}
          </Typography>
          {legalStatusLabel ? (
            <Typography color="text.secondary" variant="body2">
              Statut juridique : {legalStatusLabel}
            </Typography>
          ) : null}
          {activityLabel ? (
            <Typography color="text.secondary" variant="body2">
              Activité : {activityLabel}
            </Typography>
          ) : null}
          {result.tranche_effectif_salarie ? (
            <Typography color="text.secondary" variant="body2">
              Effectif : {result.tranche_effectif_salarie}
            </Typography>
          ) : null}
          {result.date_creation ? (
            <Typography color="text.secondary" variant="body2">
              Date de création : {formatDate(result.date_creation)}
            </Typography>
          ) : null}
          {vatNumber ? (
            <Typography color="text.secondary" variant="body2">
              TVA intracommunautaire : {vatNumber}
            </Typography>
          ) : null}
        </Stack>

        {establishments.length ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Établissements</Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {establishments.map(({ establishment, isHeadOffice }) => (
                <EstablishmentItem
                  establishment={establishment}
                  isAlreadyCreated={existingOrganizationIdentifiers.has(
                    normalizeOrganizationIdentifierValue(establishment.siret),
                  )}
                  isHeadOffice={isHeadOffice}
                  key={establishment.siret}
                  nafDesignationByCode={nafDesignationByCode}
                />
              ))}
            </Stack>
          </>
        ) : null}

        {directors.length ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Dirigeants</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {directors.map((director, index) => {
                const directorLabel = `${formatDirectorName(director)}${director.qualite ? ` - ${director.qualite}` : EMPTY_STRING}`;

                return (
                  <Stack
                    direction="row"
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    sx={{
                      alignItems: 'center',
                      gap: 1.5,
                      justifyContent: 'space-between',
                    }}
                  >
                    <EllipsisTooltip title={directorLabel}>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        variant="body2"
                      >
                        {directorLabel}
                      </Typography>
                    </EllipsisTooltip>
                    {isPhysicalPersonDirector(director) ? (
                      <Chip
                        label="sera créé comme Personne"
                        size="small"
                        sx={{ flexShrink: 0 }}
                      />
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          </>
        ) : null}
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Button
          disabled={isCreateOrganizationDisabled}
          loading={isCreateOrganizationDisabled}
          onClick={handleCreateOrganization}
          startIcon={<DomainAddIcon />}
        >
          Créer organisation
        </Button>
        <Tooltip title="Voir la réponse brute de l'API">
          <IconButton
            aria-label="Voir la réponse brute de l'API"
            onClick={openRawDataDrawer}
          >
            <IntegrationInstructionsIcon />
          </IconButton>
        </Tooltip>
      </CardActions>

      <RightDrawer
        isOpen={isRawDataDrawerOpen}
        onDrawerClose={closeRawDataDrawer}
        title="Réponse brute de l'API recherche-entreprises"
      >
        <Box sx={{ width: '100%' }}>
          <JSONPretty
            data={result}
            id={`organization-search-result-${result.siren}`}
          />
        </Box>
      </RightDrawer>
    </Card>
  );
};
